import type React from 'react';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import type { ContentData } from 'app/ui/components/molecules/content-item-areas-wrapper';

import Typography from 'app/ui/components/atoms/typography';
import SlideTimerWithControls from 'app/ui/components/molecules/slide-timer/with-controls';

type PresenterLessonSectionBreakdownProps = {
  sectionData: ContentData;
  isSmallVariant?: boolean;
  isOverviewMode?: boolean;
};

const BreakdownWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column-reverse;
  
  ${({ theme: { media } }) =>
    media.large(
      () => `
    flex-direction: row;
  `,
    )}
`;

const Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  padding-bottom: ${({ theme }) => theme.spacing(16)};

  ${({ theme: { media } }) =>
    media.large(
      () => `
    width: 50%;
    padding-bottom: 0;
  `,
    )}
`;

const SlideInfo = styled(Typography)<{ isOverviewMode?: boolean }>`
  font-weight: ${({ theme }) => theme.typography.bold};
  margin-top: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme, isOverviewMode }) => (isOverviewMode ? theme.colors.focoltone : 'transparent')};
  padding: ${({ theme, isOverviewMode }) => (isOverviewMode ? `${theme.spacing(1)} ${theme.spacing(2)}` : 0)};
  border-radius: 2px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const SlideNoteArea = styled.div`
  width: 100%;
  margin: ${({ theme }) => `${theme.spacing(3)} 0`};
  z-index: 101;
  max-height: 80vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-right: ${({ theme }) => theme.spacing(2)};
`;

const SlideNote = styled(Typography)`
  ul {
    list-style-position: inside;
    padding-left: ${({ theme }) => theme.spacing(8)};
  }

  li {
    text-indent: -1.4rem;
  }
`;

const TimerContainer = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PresenterLessonSectionBreakdown: React.FC<PresenterLessonSectionBreakdownProps> = ({
  sectionData,
  isSmallVariant = true,
  isOverviewMode,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const textVariant = isSmallVariant ? 'body3' : 'whiteboard1';

  const duration = sectionData.duration || 0;

  const notesText = intl.formatMessage({ id: 'whiteboard.notes' });
  const noNotesText = intl.formatMessage({ id: 'whiteboard.noNotes' });

  return (
    <BreakdownWrapper>
      <Column>
        <SlideInfo variant="body3" color={theme.colors.white} isOverviewMode={isOverviewMode}>
          <span>{sectionData.title}</span>
          <span>{sectionData.additionalInfo}</span>
        </SlideInfo>

        {!isOverviewMode && (
          <SlideNoteArea>
            {sectionData?.notes && sectionData.notes.trim().length > 0 ? (
              <>
                <Typography variant={textVariant} color={theme.colors.white}>
                  {notesText}:{' '}
                </Typography>
                <SlideNote variant={textVariant} color={theme.colors.white} htmlContent={sectionData.notes} />
              </>
            ) : (
              <SlideNote variant={textVariant} color={theme.colors.white}>
                {noNotesText}
              </SlideNote>
            )}
          </SlideNoteArea>
        )}
      </Column>
      <Column>
        {!isOverviewMode && (
          <TimerContainer>
            <SlideTimerWithControls finalDuration={duration} sectionData={sectionData} isPresenterView={true} />
          </TimerContainer>
        )}
      </Column>
    </BreakdownWrapper>
  );
};

export default PresenterLessonSectionBreakdown;
